import { useMemo } from 'react';
import { isEmpty } from 'lodash';

import { useClaim } from '~/components/ClaimContainer';
import { useClaimInsightsCount } from '~/components/ClaimPage/CliveApp/CliveInsights/hooks/useClaimInsightsCount';
import useClaimFieldsConfiguration from '~/components/ClaimPage/hooks/useClaimFieldsConfiguration';
import { useCms } from '~/components/hooks/useCms';
import { useLegalActions } from '~/components/LegalActions/hooks/useLegalActions';
import { useCliveClaimContext } from '~/components/SystemConfiguration/CliveConfiguration/CliveClaimContext';

import { CLAIM_FIELD_CONFIG_TYPE } from '../../types';

import { DEFAULT_SIDEBAR_TABS } from './constants';
import type { ClaimSideBarTab, SideBarKey } from './utils';
import { getClaimSideBarTabs } from './utils';

const useClaimSideBarTabs = (): ClaimSideBarTab<SideBarKey>[] => {
  const { userOrganization } = useCms();
  const { claim } = useClaim();
  const { legalActions, reloadLegalActions, hasLegalActionsPermissions } = useLegalActions();
  const { fieldsConfiguration } = useClaimFieldsConfiguration(CLAIM_FIELD_CONFIG_TYPE.CLAIM_SIDEBAR);
  const { isClaimChatEnabled, isCliveConfigEnabled } = useCliveClaimContext();
  const { undismissedCount } = useClaimInsightsCount();

  return useMemo(
    () =>
      getClaimSideBarTabs(!isEmpty(fieldsConfiguration) ? fieldsConfiguration : DEFAULT_SIDEBAR_TABS, {
        userOrganization,
        claim,
        hasLegalActionsPermissions,
        legalActions: legalActions || [],
        reloadLegalActions: reloadLegalActions || (() => Promise.resolve()),
        isClaimChatEnabled,
        isCliveInsightsTabEnabled: isCliveConfigEnabled('clive_insights_claim_tab'),
        undismissedCount,
      }),
    [
      fieldsConfiguration,
      userOrganization,
      claim,
      hasLegalActionsPermissions,
      legalActions,
      reloadLegalActions,
      isClaimChatEnabled,
      isCliveConfigEnabled,
      undismissedCount,
    ]
  );
};

export default useClaimSideBarTabs;
